import {
	createClient,
	type Session,
	type SupabaseClient,
} from "@supabase/supabase-js";
import {
	artistBackendOrigin,
	type ArtistDeploymentRegion,
} from "@/domain/auth/AuthProviderConfiguration";

export interface ArtistSupabaseConfiguration {
	readonly url: string;
	readonly publishableKey: string;
	readonly region: ArtistDeploymentRegion;
}

let client: SupabaseClient | null = null;

export function readArtistSupabaseConfiguration(): ArtistSupabaseConfiguration {
	const rawUrl = import.meta.env.VITE_SUPABASE_URL?.trim();
	const publishableKey =
		import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim();
	const region = import.meta.env.VITE_ARTIST_REGION ?? "global";
	if (!rawUrl || !publishableKey) {
		throw new Error("Supabase browser configuration is missing");
	}
	const url = artistBackendOrigin({
		VITE_ARTIST_REGION: region,
		VITE_SUPABASE_URL: rawUrl,
		VITE_SUPABASE_PUBLISHABLE_KEY: publishableKey,
	});
	return { url, publishableKey, region };
}

export function artistSupabase(): SupabaseClient {
	if (client !== null) return client;
	const configuration = readArtistSupabaseConfiguration();
	client = createClient(configuration.url, configuration.publishableKey, {
		auth: {
			flowType: "pkce",
			persistSession: true,
			autoRefreshToken: true,
			detectSessionInUrl: true,
			storageKey: "artist.supabase.auth.v1",
		},
		global: {
			headers: {
				"x-artist-client": "artist-web-static-v1",
			},
		},
	});
	return client;
}

export async function currentSupabaseSession(): Promise<Session | null> {
	const { data, error } = await artistSupabase().auth.getSession();
	if (error) throw error;
	return data.session;
}

export async function currentAccessToken(): Promise<string | null> {
	return (await currentSupabaseSession())?.access_token ?? null;
}
